import { graphql, Link, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import gsap from "gsap";
import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Col, Container, Row } from "react-bootstrap";
import { MdKeyboardArrowRight } from "react-icons/md";
import OnclickButton from "./OnclickButton";
import { SafeHtmlParser } from "./SafeHtmlParser";

const BlogsCatalogue = ({ heading, body, colour, articles }) => {
	const [activeCategory, setActiveCategory] = useState("All");
	const [categories, setCategories] = useState([]);
	const [blogs, setBlogs] = useState([]);
	const [viewAll, setViewAll] = useState(false);
	const [screenWidth, setScreenWidth] = useState(null);

	// Container ref for animation.
	const containerRef = useRef(null);

	// Fetch blogs and categories using GraphQL.
	const data = useStaticQuery(graphql`
		query BlogsCatalogueQuery {
			allWpCategory {
				nodes {
					name
					slug
					posts {
						nodes {
							id
						}
					}
				}
			}
			allWpPost {
				nodes {
					id
					title
					excerpt
					slug
					uri
					categories {
						nodes {
							name
						}
					}
					tags {
						nodes {
							name
						}
					}
					blogFields {
						numberOfMinRead
						description
						featuredImage {
							altText
							localFile {
								childImageSharp {
									gatsbyImageData(
										formats: WEBP
										quality: 80
										transformOptions: { cropFocus: CENTER, fit: COVER }
										layout: CONSTRAINED
										placeholder: BLURRED
									)
								}
							}
						}
					}
				}
			}
		}
	`);

	// Set categories and blogs.
	useEffect(() => {
		const categoryNodes = data?.allWpCategory?.nodes || [];
		const postNodes = articles || [];

		// Filter categories to exclude "Uncategorized" and ensure they have posts.
		const fetchedCategories = categoryNodes
			.filter(
				(category) =>
					category.name !== "Uncategorized" &&
					category.posts?.nodes &&
					category.posts.nodes.length > 0
			)
			.map((category) => category.name);

		setCategories(fetchedCategories);

		// Map blogs.
		const fetchedBlogs = postNodes.map((post) => ({
			id: post.id,
			title: post.title,
			excerpt: post.excerpt,
			url: post.uri,
			category: post.categories?.nodes || [],
			readTime: post.blogFields?.numberOfMinRead || 0,
			tags: post.tags?.nodes || [],
			description: post.blogFields?.description,
			featuredImage: post.blogFields?.featuredImage,
			slug: post.slug,
		}));

		setBlogs(fetchedBlogs);
	}, [data]);

	// Handle screen width.
	useEffect(() => {
		const updateScreenWidth = () => {
			setScreenWidth(window.innerWidth);
		};

		updateScreenWidth();
		window.addEventListener("resize", updateScreenWidth);
		return () => window.removeEventListener("resize", updateScreenWidth);
	}, []);

	// Filter blogs based on active category.
	const filteredBlogs =
		activeCategory === "All"
			? blogs
			: blogs.filter((blog) =>
					blog.category.some((cat) => cat.name === activeCategory)
			  );

	// Limit displayed blogs based on screen size.
	const getLimitedBlogs = () => {
		if (viewAll || !screenWidth) return filteredBlogs;
		if (screenWidth < 768) return filteredBlogs.slice(0, 2); // Mobile
		if (screenWidth < 992) return filteredBlogs.slice(0, 4); // Tablet
		return filteredBlogs.slice(0, 6); // Desktop
	};

	const limitedBlogs = getLimitedBlogs();

	// Initial animation for the intro (heading and body) on mount.
	useEffect(() => {
		setTimeout(() => {
			if (containerRef.current) {
				const tl = gsap.timeline({ defaults: { ease: "power2.out" } });
				tl.to(
					containerRef.current.querySelectorAll(".blog-intro"),
					{
						y: 0,
						opacity: 1,
						duration: 0.8,
					},
					0.1
				);
			}
		}, 100);
	}, []);

	// Re-run animation for blog cards when the category changes.
	useEffect(() => {
		if (containerRef.current) {
			setTimeout(() => {
				gsap.to(containerRef.current.querySelectorAll(".blog-card"), {
					y: 0,
					opacity: 1,
					stagger: 0.2,
					duration: 0.8,
					ease: "power2.out",
				});
			}, 100);
		}
	}, [activeCategory, limitedBlogs]);

	// Hover color animations.
	const handleMouseEnter = (e, colour) => {
		gsap.to(e.target, { color: colour, duration: 0.1 });
	};
	const handleMouseLeave = (e) => {
		gsap.to(e.target, { color: "#000", duration: 0.1 });
	};

	const handleViewAll = () => {
		setViewAll(true);
	};

	return (
		<>
			{/* Inline CSS to ensure elements start hidden */}
			<style>
				{`
          .blog-intro,
          .blog-card {
            opacity: 0;
            transform: translateY(50px);
          }
        `}
			</style>
			<section ref={containerRef} className="py-5 py-lg-7">
				<Container>
					{/* Heading */}
					{(heading || body) && (
						<Row className="text-center mb-4 ">
							<Col>
								{heading && (
									<div style={{ color: colour }}>
										<SafeHtmlParser htmlContent={heading} />
									</div>
								)}
								{body && (
									<div>
										<SafeHtmlParser htmlContent={body} />
									</div>
								)}
							</Col>
						</Row>
					)}

					{/* Category Buttons for Large Screens */}
					<div className="d-none d-lg-block">
						{categories.length > 0 && (
							<Row className="justify-content-center mb-4 mb-lg-5 mt-4 mt-lg-5">
								<Col md={10}>
									<div className="d-flex flex-wrap justify-content-center gap-3">
										<Button
											className="py-2 px-4"
											variant={
												activeCategory === "All" ? "warning" : "outline-warning"
											}
											onClick={() => setActiveCategory("All")}
											style={{
												borderRadius: "0px",
												borderColor:
													activeCategory === "All" ? colour : undefined,
												color: activeCategory === "All" ? colour : undefined,
											}}
										>
											View All
										</Button>
										{categories.map((category, index) => (
											<Button
												className="py-2 px-4"
												key={index}
												variant={
													activeCategory === category
														? "warning"
														: "outline-warning"
												}
												onClick={() => setActiveCategory(category)}
												style={{
													borderRadius: "0px",
													borderColor:
														activeCategory === category ? colour : undefined,
													color:
														activeCategory === category ? colour : undefined,
												}}
											>
												{category}
											</Button>
										))}
									</div>
								</Col>
							</Row>
						)}
					</div>

					{/* Category Buttons for Smaller Screens */}
					<div className="d-lg-none">
						{categories.length > 0 && (
							<Row className="justify-content-center mb-4 mb-lg-5 mt-4 mt-lg-5">
								<Col md={12}>
									<div
										className="d-flex gap-3"
										style={{
											overflowX: "auto",
											whiteSpace: "nowrap",
											WebkitOverflowScrolling: "touch",
											scrollbarWidth: "none",
											msOverflowStyle: "none",
											paddingBottom: "10px",
										}}
									>
										<style>
											{`
                        div::-webkit-scrollbar {
                          display: none;
                        }
                      `}
										</style>
										<Button
											className="py-2 px-4 d-inline-block"
											variant={
												activeCategory === "All" ? "warning" : "outline-warning"
											}
											onClick={() => setActiveCategory("All")}
											style={{
												borderRadius: "0px",
												borderColor:
													activeCategory === "All" ? colour : undefined,
												color: activeCategory === "All" ? colour : undefined,
											}}
										>
											View All
										</Button>
										{categories.map((category, index) => (
											<Button
												key={index}
												className="py-2 px-4 d-inline-block"
												variant={
													activeCategory === category
														? "warning"
														: "outline-warning"
												}
												onClick={() => setActiveCategory(category)}
												style={{
													borderRadius: "0px",
													borderColor:
														activeCategory === category ? colour : undefined,
													color:
														activeCategory === category ? colour : undefined,
												}}
											>
												{category}
											</Button>
										))}
									</div>
								</Col>
							</Row>
						)}
					</div>

					{/* Blog Cards */}
					<Row>
						{limitedBlogs.length > 0 ? (
							limitedBlogs.map((blog) => {
								const image = getImage(
									blog.featuredImage?.localFile?.childImageSharp
										?.gatsbyImageData
								);
								const firstTag = blog.tags[0]?.name; // Get the first tag, if available
								return (
									<Col
										sm={12}
										md={6}
										lg={4}
										xs={12}
										key={blog.id}
										className="mb-4"
									>
										<Card
											style={{
												borderRadius: "14px",
												overflow: "hidden",
												border: "1px solid #CEE4FF",
											}}
											className="h-100 blog-card"
										>
											{/* Blog Image */}
											{image && (
												<GatsbyImage
													image={image}
													alt={blog.featuredImage?.altText || "Blog image"}
													className="card-img-top"
													style={{
														height: "270px",
														objectFit: "cover",
													}}
												/>
											)}
											<Card.Body className="p-4 position-relative">
												{/* Tag and Read Time */}
												<div className="d-flex align-items-center mb-3">
													{firstTag && (
														<div className="p-1 px-2 position-relative">
															<div
																style={{ background: colour, opacity: 0.4 }}
																className="position-absolute w-100 h-100 start-0 top-0"
															></div>
															<p
																style={{ zIndex: 2 }}
																className="outfit-regular position-relative py-0 my-0"
															>
																{firstTag}
															</p>
														</div>
													)}
													{blog.readTime && (
														<p className="py-0 my-0 ps-3">
															{blog.readTime} min read
														</p>
													)}
												</div>
												{/* Blog Title */}
												<h5>{blog.title}</h5>
												{blog.description && <p>{blog.description}</p>}
												<div style={{ height: "30px" }} className="w-100"></div>
												<div className="d-flex align-items-center mt-4 position-absolute bottom-0 start-0 p-4">
													<Link
														onMouseEnter={(e) => handleMouseEnter(e, colour)}
														style={{ color: "black" }}
														onMouseLeave={handleMouseLeave}
														to={`/resources/${blog.slug}`}
														className="outfit-regular d-block text-decoration-none"
													>
														Read More
													</Link>
													<span style={{ color: colour }}>
														<MdKeyboardArrowRight className="fs-5 ms-1" />
													</span>
												</div>
											</Card.Body>
										</Card>
									</Col>
								);
							})
						) : (
							<Col>
								<p className="text-center">No blogs available at this time.</p>
							</Col>
						)}
					</Row>

					{/* View All Button */}
					{filteredBlogs.length > limitedBlogs.length && (
						<Row className="text-center mt-3 mt-md-4">
							<Col>
								{!viewAll && (
									<OnclickButton
										width="w-100 w-md-auto"
										colour={colour}
										text="View All"
										isOutline={true}
										onClick={() => setViewAll(true)}
									/>
								)}
							</Col>
						</Row>
					)}
				</Container>
			</section>
		</>
	);
};

export default BlogsCatalogue;
